import clsx from "clsx";
import { useState } from "react";

import { FieldType } from "@smart/bridge-types-basic";
import {
  Button,
  Modal,
  Switch,
  Tooltip,
} from "@smart/itops-sb-design-system-dom";

import { ModifyNameFieldModal } from "./modify-name-field-modal";

type ActionsProps = {
  type: FieldType;
  descriptionVisible: boolean;
  mandatory: boolean;
  includeInSummary: boolean;
  onToggleDescription: (visible: boolean) => void;
  onToggleMandatory: (mandatory: boolean) => void;
  onToggleIncludeInSummary: (checked: boolean) => void;
  onDelete: () => void;
  isViewingBaseLanguage: boolean;
  shouldShowWebEnquiryWarning: boolean;
};

export const Actions = ({
  type,
  descriptionVisible,
  mandatory,
  includeInSummary,
  onToggleDescription,
  onToggleMandatory,
  onToggleIncludeInSummary,
  onDelete,
  isViewingBaseLanguage,
  shouldShowWebEnquiryWarning,
}: ActionsProps) => {
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [modifyNameModal, setModifyNameModal] = useState<
    "delete" | "notRequired" | null
  >(null);

  const isInfo = type === "info";

  const openDeleteFlow = () => {
    if (shouldShowWebEnquiryWarning) {
      setModifyNameModal("delete");
    } else {
      setIsDeleteModalOpen(true);
    }
  };

  const onRequiredSwitchClick = () => {
    if (mandatory && shouldShowWebEnquiryWarning) {
      setModifyNameModal("notRequired");
    } else {
      onToggleMandatory(!mandatory);
    }
  };

  return (
    <div
      className={clsx(
        "flex gap-small h-full justify-end items-start flex-wrap",
        isInfo ? "w-[26.5rem]" : "w-[21rem]",
      )}
    >
      <div className="h-full flex flex-wrap items-start pt-[0.8rem]">
        <Tooltip
          title={isInfo ? undefined : "Required field"}
          text={
            isInfo
              ? "When toggled on, this text will also be included in the downloaded copy and saved response record."
              : "Toggle 'on' to ensure your client completes the field before submitting."
          }
          placement="top-end"
          offsetBy={7}
          variant="paragraphy"
        >
          {isInfo ? (
            <Switch
              text="Include in saved record"
              toggled={includeInSummary}
              onClick={() => onToggleIncludeInSummary(!includeInSummary)}
              disabled={!isViewingBaseLanguage}
            />
          ) : (
            <Switch
              text="Required"
              toggled={mandatory}
              onClick={onRequiredSwitchClick}
              disabled={!isViewingBaseLanguage}
            />
          )}
        </Tooltip>
      </div>
      {!isInfo && (
        <Tooltip
          title="Description text"
          text="Add a description to guide clients on what to enter."
          placement="top-end"
          offsetBy={7}
          variant="paragraphy"
        >
          <Button
            variant="secondarySubtle"
            leftIcon="regularText"
            onClick={() => onToggleDescription(!descriptionVisible)}
            size="small"
            isPressed={descriptionVisible}
            dataTestId="toggle-description"
            disabled={!isViewingBaseLanguage}
          />
        </Tooltip>
      )}
      <Tooltip
        title="Delete component"
        text="Click to delete component"
        placement="top-end"
        offsetBy={7}
        variant="paragraphy"
      >
        <Button
          variant="secondarySubtle"
          leftIcon="regularTrashCan"
          onClick={openDeleteFlow}
          size="small"
          dataTestId="delete-field"
          disabled={!isViewingBaseLanguage}
        />
      </Tooltip>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        header={{
          icon: "solidTrashCan",
          text: "Delete item",
        }}
        footer={{
          buttons: [
            {
              variant: "secondarySubtle",
              text: "Cancel",
              onClick: () => setIsDeleteModalOpen(false),
            },
            {
              variant: "primaryDefault",
              text: "Yes, delete",
              onClick: () => {
                setIsDeleteModalOpen(false);
                onDelete();
              },
            },
          ],
        }}
      >
        Are you sure you want to delete the item?
      </Modal>

      <ModifyNameFieldModal
        type={modifyNameModal || "notRequired"}
        open={!!modifyNameModal}
        onClose={() => setModifyNameModal(null)}
        onConfirm={async () => {
          if (modifyNameModal === "delete") {
            onDelete();
          } else {
            await onToggleMandatory(false);
          }
          setModifyNameModal(null);
        }}
      />
    </div>
  );
};
